/** Static, authenticated app destinations available to the GA page catalog. */
export interface PageCatalogSettingsPermissions {
  canManageBilling: boolean;
  canViewBilling: boolean;
  canViewStorageLimits: boolean;
  showFilePermissions: boolean;
}

export type PageDefinition = {
  id: string;
  label: string;
  route: string;
  searchTerms: readonly string[];
  /** Shown with search results when a search term alone could mislead. */
  note?: string;
};

/**
 * Labels mirror the visible sidebar and workspace-menu names (Home, Connect,
 * Schedule, ...), and a search term or label belongs to exactly one page so a
 * spoken name never resolves by alphabetical tie-break. The sidebar's My Drive
 * and Apps links depend on the requester's home and are added in
 * `page_catalog.helpers.ts`.
 */
export const WORKSPACE_PAGES: readonly PageDefinition[] = [
  {
    // The bare workspace URL only redirects to Home (/design).
    id: 'workspace-home',
    label: 'Workspace dashboard',
    route: '',
    searchTerms: ['dashboard'],
  },
  {
    // The sidebar's "Tasks" link (formerly "Home").
    id: 'design',
    label: 'Tasks',
    route: '/design',
    searchTerms: [
      'tasks',
      'my tasks',
      'all tasks',
      'task list',
      'new task',
      'home',
      'my home',
      'home page',
      'homepage',
      'home screen',
      'landing',
      'landing page',
      'landing screen',
      'main page',
      'main screen',
      'design',
      'create',
      'start',
      'new chat',
      'design studio',
    ],
  },
  {
    id: 'threads',
    label: 'Chats',
    route: '/threads',
    searchTerms: ['threads', 'conversations', 'chat history'],
  },
  {
    // The sidebar's "Connect" link (copy key `inbox`).
    id: 'work-chats',
    label: 'Connect',
    route: '/chats',
    searchTerms: [
      'connect',
      'inbox',
      'messages',
      'work chats',
      'team chat',
      'channels',
      'dms',
      'direct messages',
    ],
  },
  {
    id: 'activity',
    label: 'Activity',
    route: '/activity',
    searchTerms: ['notifications', 'alerts', 'unread', 'needs my answer'],
  },
  {
    id: 'scheduled',
    label: 'Schedule',
    route: '/scheduled',
    searchTerms: [
      'schedule',
      'my schedule',
      'calendar',
      'my calendar',
      'calendar view',
      'schedule calendar',
      'calendar schedule',
      'task schedule',
      'task calendar',
      'scheduled',
      'scheduled task',
      'scheduled tasks',
      'my scheduled tasks',
      'scheduled jobs',
      'scheduled automations',
      'automations',
      'recurring',
      'recurring tasks',
    ],
    note: "Lists IMAI's scheduled agent tasks. It does not show the user's Google Calendar events or email. Reading those is a task for the user's connected account, or an offer to connect it when it is not connected, not navigation.",
  },
  {
    id: 'products',
    label: 'Products',
    route: '/products',
    searchTerms: ['my products', 'product studio'],
  },
  {
    id: 'new-product',
    label: 'New product',
    route: '/products/add',
    searchTerms: ['add product', 'create product'],
  },
  {
    id: 'studio',
    label: 'Studio',
    route: '/studio',
    searchTerms: ['lookbooks', 'creative studio'],
  },
  {
    id: 'aesthetics',
    label: 'Aesthetics',
    route: '/aesthetics',
    searchTerms: ['styles', 'moodboards'],
  },
  {
    id: 'explore',
    label: 'Explore',
    route: '/explore',
    searchTerms: ['discover', 'inspiration'],
  },
  {
    id: 'documents',
    label: 'Documents',
    route: '/documents',
    searchTerms: ['docs', 'document library'],
  },
  {
    id: 'projects',
    label: 'Projects',
    route: '/projects',
    searchTerms: ['project folders'],
  },
  {
    id: 'library',
    label: 'Library',
    route: '/library',
    searchTerms: ['asset library', 'saved media'],
  },
  {
    id: 'media-files',
    label: 'Media files',
    route: '/files/media',
    searchTerms: ['media library', 'images', 'videos'],
  },
  {
    id: 'product-files',
    label: 'Product files',
    route: '/files/products',
    searchTerms: ['product folders', 'product files'],
  },
  {
    id: 'avatars',
    label: 'Avatars',
    route: '/files/avatars',
    searchTerms: ['avatar files', 'models', 'characters'],
  },
  {
    id: 'new-avatar',
    label: 'New avatar',
    route: '/avatars/new',
    searchTerms: ['create avatar', 'add avatar'],
  },
  {
    id: 'brands',
    label: 'Brands',
    route: '/files/brands',
    searchTerms: ['brand', 'brand files', 'brand folders'],
  },
  {
    id: 'workspace-files',
    label: 'Workspace files',
    route: '/files',
    searchTerms: ['file browser', 'files', 'folders'],
  },
  {
    id: 'shared-files',
    label: 'Shared files',
    route: '/files/shared-with-me',
    searchTerms: ['files shared with me', 'file shares'],
  },
  {
    id: 'user-homes',
    label: 'User homes',
    route: '/files/users',
    searchTerms: ['home folders', 'users files'],
  },
  {
    id: 'drives',
    label: 'Drives',
    route: '/drives',
    searchTerms: ['shared drives', 'all drives'],
  },
  {
    id: 'workspace-library-drive',
    label: 'Workspace library drive',
    route: '/drives/library',
    searchTerms: ['library drive', 'workspace library'],
  },
  {
    id: 'shared-with-me',
    label: 'Shared with me',
    route: '/drives/shared-with-me',
    searchTerms: ['incoming shares', 'shared with me drive'],
  },
  {
    id: 'team-drives',
    label: 'Team drives',
    route: '/drives/teams',
    searchTerms: ['team files', 'shared team folders'],
  },
  {
    id: 'project-drives',
    label: 'Project drives',
    route: '/drives/projects',
    // "Project folders" belongs to the sidebar's Projects page.
    searchTerms: ['project files', 'project drive'],
  },
  {
    // The member's own texting-style chat with their IMAI (sidebar entry).
    id: 'your-imai',
    label: 'Your IMAI',
    route: '/imai',
    searchTerms: ['your imai', 'my imai', 'imai chat', 'personal assistant'],
  },
  {
    // Name, look, context resets and personality of the member's IMAI.
    id: 'your-imai-settings',
    label: 'Your IMAI settings',
    route: '/imai/settings/customize',
    searchTerms: [
      'imai settings',
      'your imai settings',
      'imai personality',
      'reset imai chat',
    ],
  },
  {
    id: 'connectors',
    label: 'Connectors',
    route: '/connectors',
    searchTerms: [
      'connectors',
      'integrations',
      'connections',
      'connected apps',
    ],
  },
  {
    id: 'downloads',
    label: 'Downloads',
    route: '/downloads',
    searchTerms: ['downloads', 'downloaded files', 'exports'],
  },
  {
    id: 'trash',
    label: 'Trash',
    route: '/trash',
    searchTerms: ['trash', 'bin', 'deleted files', 'recycle bin'],
  },
  {
    id: 'support',
    label: 'Support',
    route: '/support',
    searchTerms: ['support', 'help', 'help desk', 'tickets'],
  },
  {
    id: 'trend-analysis',
    label: 'Trend analysis',
    route: '/trend-analysis',
    searchTerms: ['trends', 'market research'],
  },
  {
    id: 'branding',
    label: 'Branding',
    route: '/branding',
    searchTerms: ['brand studio', 'brand analysis'],
  },
  {
    id: 'brand-time-machine',
    label: 'Brand time machine',
    route: '/branding/time-machine',
    searchTerms: ['brand history', 'brand timeline'],
  },
];

export {
  PERSONAL_SETTINGS,
  WORKSPACE_SETTINGS,
} from './page_catalog_settings.helpers';
